// VAT Desync Section (STA-5 · Feb 2026)
// ─────────────────────────────────────────────────────────────────
// Standalone card that surfaces bills counted by a filed/prepared
// VAT Return whose GL journal is missing a VAT_INPUT line.
//
// Anchored on a specific `returnId` — completely independent of the
// Reconciliation Center's active period. Auto-diagnoses on mount.
// Admin users see a "Backfill" action; non-admins see a read-only
// forensic view.
import React, { useCallback, useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  ShieldAlert, CheckCircle2, RefreshCw, Loader2, Wrench,
} from 'lucide-react';
import { Button } from './ui/button';

const fmt = (n) => {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
};

const REASON_LABEL = {
  no_journal_no:     'Bill has no journal_no',
  missing_journal:   'Journal referenced but not found',
  no_vat_input_line: 'Journal exists but has no VAT_INPUT line',
  amount_mismatch:   'GL VAT amount differs from bill VAT',
};

const SOURCE_LABEL = {
  supplier_bill:   'Bill',
  expense_report:  'Expense',
  bank_adjustment: 'Bank Adj',
};

export default function VATDesyncSection({ returnId, filed = false, compact = false }) {
  const [data, setData] = useState(null);
  const [hvd, setHvd] = useState(null);      // header-vs-details report
  const [loading, setLoading] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [error, setError] = useState(null);

  const runDiagnose = useCallback(async () => {
    if (!returnId) return;
    setLoading(true);
    setError(null);
    try {
      const [r1, r2] = await Promise.all([
        axiosInstance.get(`/vat/returns/${returnId}/desync/diagnose`),
        axiosInstance.get(`/vat/returns/${returnId}/desync/header-vs-details`)
          .catch(() => ({ data: null })),
      ]);
      setData(r1.data);
      setHvd(r2.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Diagnose failed';
      setError(msg);
    } finally { setLoading(false); }
  }, [returnId]);

  useEffect(() => { runDiagnose(); }, [runDiagnose]);

  const runRebuild = async (alsoRecomputeHeader) => {
    if (!returnId) return;
    const confirmMsg = alsoRecomputeHeader
      ? 'إعادة بناء تفاصيل الإقرار + تحديث رأس (Header) input_vat من المصادر الحية؟\n\n'
        + 'القيمة الأصلية المُقدَّمة تُحفظ في input_vat_original للـ audit.'
      : 'إعادة بناء details.purchases من المصادر الحية فقط (بدون تعديل الرأس)؟';
    if (!window.confirm(confirmMsg)) return;
    setRebuilding(true);
    try {
      const r = await axiosInstance.post(
        `/vat/returns/${returnId}/desync/rebuild-snapshot`
          + `?also_recompute_header=${alsoRecomputeHeader}`);
      toast.success(
        `تم إعادة البناء: ${r.data.purchases_count} حركة · Total VAT=${fmt(r.data.total_vat)}`
        + (alsoRecomputeHeader && r.data.header_delta !== null
            ? ` · Header Δ=${fmt(r.data.header_delta)}`
            : '')
      );
      await runDiagnose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rebuild failed');
    } finally { setRebuilding(false); }
  };

  const runBackfill = async () => {
    if (!data?.orphan_count) return;
    if (!window.confirm(
      `Post corrective VAT_INPUT lines to ${data.orphan_count} bill journal(s)?\n\n`
      + `This will restore GL ↔ Sub-ledger parity.\n`
      + `The original bill amounts are NEVER modified.`)) return;
    setRepairing(true);
    try {
      const r = await axiosInstance.post(
        `/vat/returns/${returnId}/desync/backfill`);
      const rep = r.data.repaired || [];
      const skp = r.data.skipped  || [];
      const ac  = r.data.auto_closed;
      toast.success(
        `Repaired ${rep.length} journal(s) · added ${fmt(r.data.total_added)} SAR to GL`
        + (skp.length ? ` · تم تخطي ${skp.length}` : '')
        + (ac         ? ` · تم إقفال تلقائي: ${fmt(ac.amount)} SAR` : ''));
      if (ac) {
        toast.info(`✨ Auto-close · ${ac.journal_no} · ${ac.note}`,
                    { duration: 8000 });
      }
      // Feb 2026 · surface skip reasons so the user sees WHY nothing
      // moved. Group by reason and toast up to 3 distinct ones.
      if (skp.length) {
        const grouped = {};
        skp.forEach(s => {
          const key = (s.skip_reason || 'unknown').split('·')[0].trim();
          grouped[key] = (grouped[key] || 0) + 1;
        });
        Object.entries(grouped).slice(0, 3).forEach(([k, n]) => {
          toast.warning(`${n} × ${k}`);
        });
      }
      setData(r.data.diagnostic);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backfill failed');
    } finally { setRepairing(false); }
  };

  if (!returnId) return null;

  if (loading && !data) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 flex items-center gap-2 text-sm text-slate-600"
           data-testid="vat-desync-section-loading">
        <Loader2 size={14} className="animate-spin" />
        Scanning bills for GL desync…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4"
           data-testid="vat-desync-section-error">
        <div className="flex items-center justify-between">
          <div className="text-xs text-slate-600">
            Desync scan unavailable: <span className="font-mono">{error}</span>
          </div>
          <Button size="sm" variant="outline" onClick={runDiagnose}>
            <RefreshCw size={12} className="mr-1" /> Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const clean = data.orphan_count === 0;
  const container = clean
    ? 'border-emerald-200 bg-emerald-50'
    : 'border-rose-200 bg-rose-50';

  return (
    <div className={`rounded-lg border ${container} p-4`}
         data-testid="vat-desync-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {clean
            ? <CheckCircle2 size={16} className="text-emerald-700" />
            : <ShieldAlert size={16} className="text-rose-700" />}
          <div>
            <div className={`text-sm font-bold ${clean ? 'text-emerald-800' : 'text-rose-800'}`}>
              {clean
                ? 'Sub-Ledger ↔ GL VAT: fully reconciled'
                : `${data.orphan_count} orphan doc(s) — GL missing ${fmt(data.unattributed_gap)} SAR of VAT_INPUT`}
            </div>
            <div className="text-[11px] text-slate-600 mt-0.5">
              {clean
                ? 'Every entry counted by this return has a matching VAT_INPUT line in the GL.'
                : 'These bills/expenses/adjustments fed the return via their sub-ledger vat_amount but their journal has no VAT_INPUT line.'}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={runDiagnose} disabled={loading}
                  data-testid="btn-desync-rescan">
            <RefreshCw size={12} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
            Re-scan
          </Button>
          {!clean && (
            <Button size="sm" variant="destructive" onClick={runBackfill}
                    disabled={repairing} data-testid="btn-desync-backfill">
              <Wrench size={12} className="mr-1" />
              {repairing ? 'Repairing…' : `Backfill ${data.orphan_count}`}
            </Button>
          )}
        </div>
      </div>

      {!compact && (
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <StatCell label="Sub-Ledger VAT" value={fmt(data.total_bill_vat)}    tone="slate" />
          <StatCell label="GL VAT total"    value={fmt(data.total_journal_vat)} tone="slate" />
          <StatCell label="Unattributed gap" value={fmt(data.unattributed_gap)}  tone={clean ? 'emerald' : 'rose'} />
          <StatCell label="Orphan docs"     value={data.orphan_count}          tone={clean ? 'emerald' : 'rose'} />
        </div>
      )}

      {/* Feb 2026 — Header vs Details vs Live three-way panel.
          Explains cases where Deep-Diagnose sees "0 orphans" but the
          reconciliation report still shows a big Return↔GL variance —
          typical when the FILED HEADER was computed from a different
          source than the persisted DETAILS. */}
      {hvd && (
        <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-3"
              data-testid="vat-header-vs-details-panel">
          <div className="text-xs font-bold text-amber-900 mb-2">
            🔬 Header ↔ Details ↔ Live — تحليل ثلاثي
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
            <StatCell label="A · Filed HEADER"
                       value={`${fmt(hvd.header_input_vat)} SAR`}
                       tone="slate" />
            <StatCell label="B · Snapshot DETAILS"
                       value={`${fmt(hvd.details_sum)} SAR`}
                       tone="slate" />
            <StatCell label="C · LIVE re-aggregated"
                       value={`${fmt(hvd.live_sum)} SAR`}
                       tone="slate" />
          </div>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2 text-[11px]">
            <div className="rounded bg-white border border-slate-200 p-2">
              <div className="text-slate-500">Header − Details</div>
              <div className={`font-bold ${Math.abs(hvd.header_vs_details) < 0.05 ? 'text-emerald-700' : 'text-rose-700'}`}>
                {fmt(hvd.header_vs_details)}
              </div>
            </div>
            <div className="rounded bg-white border border-slate-200 p-2">
              <div className="text-slate-500">Header − Live</div>
              <div className={`font-bold ${Math.abs(hvd.header_vs_live) < 0.05 ? 'text-emerald-700' : 'text-rose-700'}`}>
                {fmt(hvd.header_vs_live)}
              </div>
            </div>
            <div className="rounded bg-white border border-slate-200 p-2">
              <div className="text-slate-500">Details − Live</div>
              <div className={`font-bold ${Math.abs(hvd.details_vs_live) < 0.05 ? 'text-emerald-700' : 'text-rose-700'}`}>
                {fmt(hvd.details_vs_live)}
              </div>
            </div>
          </div>
          <div className="mt-2 text-[11px] text-amber-900 bg-amber-100 border border-amber-200 rounded px-2 py-1">
            <b>Diagnosis:</b> {hvd.diagnosis}
          </div>
          <div className="mt-2 text-[11px] text-slate-700">
            <b>هل يوجد مصادر لم تُدرَج في Details؟</b>{' '}
            {Object.keys(hvd.by_source_live || {}).length
              ? Object.entries(hvd.by_source_live).map(([k, v]) => (
                  <span key={k} className="inline-block mr-2">
                    <b>{k}</b>: {v.count} · {fmt(v.vat)}
                  </span>
                )) : '—'}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" variant="outline"
                     onClick={() => runRebuild(false)}
                     disabled={rebuilding} data-testid="btn-rebuild-details-only">
              {rebuilding ? 'يعيد البناء…' : '↻ إعادة بناء Details فقط'}
            </Button>
            <Button size="sm" variant="destructive"
                     onClick={() => runRebuild(true)}
                     disabled={rebuilding} data-testid="btn-rebuild-with-header">
              {rebuilding ? 'يعيد البناء…' : '⚠ إعادة بناء + تحديث Header'}
            </Button>
          </div>
          <div className="mt-2 text-[10px] text-slate-500 leading-relaxed">
            {hvd.next_action_hint}
          </div>
        </div>
      )}


      {!clean && data.orphans?.length > 0 && (
        <div className="mt-3 max-h-72 overflow-y-auto bg-white rounded border border-rose-200">
          <table className="w-full text-[11px]" data-testid="desync-orphans-table">
            <thead className="bg-rose-100 text-rose-900 sticky top-0">
              <tr>
                <th className="text-left px-2 py-1">Source</th>
                <th className="text-left px-2 py-1">Doc #</th>
                <th className="text-left px-2 py-1">Date</th>
                <th className="text-left px-2 py-1">Party</th>
                <th className="text-left px-2 py-1">Journal #</th>
                <th className="text-right px-2 py-1">Sub-Ledger VAT</th>
                <th className="text-right px-2 py-1">GL VAT</th>
                <th className="text-right px-2 py-1">Δ</th>
                <th className="text-left px-2 py-1">Reason</th>
              </tr>
            </thead>
            <tbody>
              {data.orphans.map((o, i) => (
                <tr key={i} className="border-t border-rose-100"
                    data-testid={`desync-orphan-${o.bill_number || i}`}>
                  <td className="px-2 py-1 text-[10px] font-semibold text-slate-700">
                    {SOURCE_LABEL[o.source] || o.source || 'Bill'}
                  </td>
                  <td className="px-2 py-1 font-mono">{o.bill_number || '—'}</td>
                  <td className="px-2 py-1 font-mono">{o.bill_date || '—'}</td>
                  <td className="px-2 py-1">{o.supplier_name || '—'}</td>
                  <td className="px-2 py-1 font-mono text-slate-500">{o.journal_no || '—'}</td>
                  <td className="px-2 py-1 font-mono text-right">{fmt(o.bill_vat_amount)}</td>
                  <td className="px-2 py-1 font-mono text-right">{fmt(o.gl_vat_amount)}</td>
                  <td className="px-2 py-1 font-mono text-right text-rose-700 font-bold">{fmt(o.delta)}</td>
                  <td className="px-2 py-1 text-[10px] text-slate-600">
                    {REASON_LABEL[o.reason] || o.reason}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!clean && filed && (
        <p className="mt-2 text-[11px] text-slate-600 italic">
          Note: this return is <b>filed</b>. Backfill posts corrective GL lines only —
          the immutable filed snapshot is never modified.
        </p>
      )}
    </div>
  );
}

function StatCell({ label, value, tone = 'slate' }) {
  const toneCls = {
    slate:   'text-slate-800',
    rose:    'text-rose-700',
    emerald: 'text-emerald-700',
  }[tone];
  return (
    <div className="bg-white/70 rounded p-2 border border-slate-200">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`font-mono font-bold text-sm mt-0.5 ${toneCls}`}>{value}</div>
    </div>
  );
}
